import { useTheme } from '@mui/material/styles';
import { URLS } from '../../config/app-config';
// File: src/components/navigation/SideNavigation.jsx

import React, {
    useState, useMemo, useRef, useEffect, useCallback,
} from 'react';
import {
    Drawer,
    Box,
    Snackbar,
    LinearProgress,
    useMediaQuery,
} from '@mui/material';
import TocList from './toc/TocList';
import TocHeaderBar from './toc/TocHeaderBar';

import { LLM_STATES, useLLMProcess } from '../../contexts/LLMProcessContext';

import { secureFetch } from "../../utils/secureFetch";
import { useAuth } from "../../auth/AuthProvider";
import { generatePdf, generateSummary } from "../../utils/jobExecution";

/* ------------------------------------------------------------------ */
/* Backend-URL                                                        */
/* ------------------------------------------------------------------ */
const base = URLS.apiBase.replace(/\/$/, '');
const defaultOpenLevels = 0;

// Mode selection
export const SIDENAV_MODES = {
    none: 'none',
    print: 'print',
    llm: 'llm',
};

export default function SideNavigation(props) {
    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';
    /* Props */
    const {
        mobileOpen,
        handleDrawerToggle,
        tocData,
        onArticleSelect: openArticle,
        selectedArticleId,
        selectedRelease,
        drawerWidth,
        onResize,
        toggleDarkMode,
    } = props;

    /* ──────────────────────────────────────────────────────────────── */
    /* LLM-Context                                                     */
    /* ──────────────────────────────────────────────────────────────── */
    const {
        setSelected,
        setProgress,
        status, setStatus,
        resultUrl, setResultUrl,
        setResultMarkdown,
        setStartProcess,
        reset: resetLLM,
    } = useLLMProcess();

    /* ──────────────────────────────────────────────────────────────── */
    /* Local state / refs                                              */
    /* ──────────────────────────────────────────────────────────────── */
    // The side nav uses two distinct elements for displaying on mobile / desktop
    // Autoscroll
    const mobileScrollRef = useRef(null);
    const desktopScrollRef = useRef(null);
    const resizeHandleRef = useRef(null);
    const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    // Jobs
    const jobAbortRef = useRef(null);
    const jobRef = useRef(null);
    const [jobResultUrl, setJobResultUrl] = useState(null);

    // Mode handling
    const [mode, setMode] = useState(SIDENAV_MODES.none); // 'print' | 'llm' | 'none'
    const [jobActive, setJobActive] = useState(false);
    const [jobProgress, setJobProgress] = useState(0);

    // Selection handling
    const [selectingAll, setSelectingAll] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]); // ← User selected Topic-IDs

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState('');

    const { isAdmin } = useAuth();

    const notifyError = useCallback(msg => {
        setSnackbarMsg(msg);
        setSnackbarOpen(true);
    }, []);

    /* ──────────────────────────────────────────────────────────────── */
    /*   helper maps                                                   */
    /* ──────────────────────────────────────────────────────────────── */
    const { nodeMap, parentMap } = useMemo(() => {
        const nodesById = new Map();
        const parentsById = new Map();
        const walk = (nodes, parent = null) => {
            nodes.forEach(node => {
                nodesById.set(node._id, node);
                if (parent) parentsById.set(node._id, parent._id);
                if (node.children) walk(node.children, node);
            });
        };
        walk(tocData);
        return { nodeMap: nodesById, parentMap: parentsById };
    }, [tocData]);

    // Keep topic labels synchronized without resetting a running job on progress updates.
    useEffect(() => {
        setSelected(selectedIds.map(id => ({ _id: id, title: nodeMap.get(id)?.title || id })));
    }, [nodeMap, selectedIds, setSelected]);

    useEffect(() => {
        if (mode === SIDENAV_MODES.llm) {
            setStatus(current => current === LLM_STATES.idle ? current : LLM_STATES.config);
        }
    }, [mode, selectedIds, setStatus]);

    /* ──────────────────────────────────────────────────────────────── */
    /*  Resize handle                                                  */
    /* ──────────────────────────────────────────────────────────────── */
    const startResize = useCallback((e) => {
        if (e.pointerType && !['mouse', 'pen'].includes(e.pointerType)) return;
        const sx = e.clientX, sw = drawerWidth;
        const mv = ev => onResize(sw + (ev.clientX - sx));
        const up = () => {
            window.removeEventListener('pointermove', mv);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', mv);
        window.addEventListener('pointerup', up);
    }, [drawerWidth, onResize]);

    /* ──────────────────────────────────────────────────────────────── */
    /*   active path / subtree utils                                   */
    /* ──────────────────────────────────────────────────────────────── */
    const activePath = useMemo(() => {
        const path = [];
        let cur = selectedArticleId;
        while (cur) { path.unshift(cur); cur = parentMap.get(cur); }
        return path;
    }, [selectedArticleId, parentMap]);

    /*  toggles a whole subtree (checkbox-style)  */
    /*  toggles a whole subtree (server-backed; works with lazy-loaded trees)  */
    const toggleSubtree = async (id) => {
        try {
            const node = nodeMap.get(id);
            const topicId = node?.topic_id; // preferred if present

            const qs = selectedRelease.toSearchParams({ include_self: 'true' });
            if (topicId) {
                qs.set('topic_id', String(topicId));
            } else {
                qs.set('node_id', String(id));
                qs.set('by', '_id');
            }

            const res = await secureFetch(`${base}/toc/subtree-article-ids?${qs.toString()}`, {
                credentials: 'include'
            });
            if (!res.ok) throw new Error(await res.text());
            const { toc_node_ids } = await res.json();

            setSelectedIds(prev => {
                const all = toc_node_ids.every(x => prev.includes(x));
                const next = all
                    ? prev.filter(x => !toc_node_ids.includes(x))
                    : Array.from(new Set([...prev, ...toc_node_ids]));

                if (mode === 'llm') {
                    setSelected(next.map(x => ({ _id: x, title: nodeMap.get(x)?.title || x })));
                }
                return next;
            });
        } catch (err) {
            notifyError(err.message || 'Toggle failed');
        }
    };

    /* ──────────────────────────────────────────────────────────────── */
    /*   Auto-scroll to current topic                                  */
    /* ──────────────────────────────────────────────────────────────── */
    const getActiveScrollBox = useCallback(() => {
        // Returns the first scrollable element in the DOM, or null if none is found.
        const refs = [desktopScrollRef.current, mobileScrollRef.current];
        return refs.find((el) => {
            if (!el) return false;
            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        }) || null;
    }, []);

    useEffect(() => {
        const box = getActiveScrollBox();
        if (!box || !selectedArticleId) return;

        let settleTimer = null;
        let firstFrame = null;
        let secondFrame = null;

        const scrollToSelected = () => {
            const el = box.querySelector(
                `[data-id="${CSS.escape(String(selectedArticleId))}"]`
            );

            if (!el) return;

            const boxRect = box.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();

            resizeObserver.disconnect();
            mutationObserver.disconnect();

            const elementCenter =
                elRect.top - boxRect.top + elRect.height / 2;

            const targetTop = Math.max(
                0,
                Math.min(
                    box.scrollTop + elementCenter - box.clientHeight / 2,
                    box.scrollHeight - box.clientHeight
                )
            );

            box.scrollTo({
                top: targetTop,
                behavior: reduceMotion ? 'instant' : 'smooth',
            });
        };

        const scheduleScroll = () => {
            window.clearTimeout(settleTimer);
            window.cancelAnimationFrame(firstFrame);
            window.cancelAnimationFrame(secondFrame);

            // ResizeObserver continues firing throughout branch animations.
            // This runs only after no size change has happened for 100 ms.
            settleTimer = window.setTimeout(() => {
                firstFrame = window.requestAnimationFrame(() => {
                    secondFrame = window.requestAnimationFrame(scrollToSelected);
                });
            }, 100);
        };

        const tocContent = box.firstElementChild;

        const resizeObserver = new ResizeObserver(scheduleScroll);
        if (tocContent) {
            resizeObserver.observe(tocContent);
        }

        // Handles active branches being mounted for the first time.
        const mutationObserver = new MutationObserver(scheduleScroll);
        mutationObserver.observe(box, {
            childList: true,
            subtree: true,
        });

        scheduleScroll();

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            window.clearTimeout(settleTimer);
            window.cancelAnimationFrame(firstFrame);
            window.cancelAnimationFrame(secondFrame);
        };
    }, [
        selectedArticleId,
        activePath,
        mobileOpen,
        getActiveScrollBox,
        reduceMotion,
    ]);

    /* ──────────────────────────────────────────────────────────────── */
    /*   Mode handling                                                 */
    /* ──────────────────────────────────────────────────────────────── */
    const handlePrintClick = () => {
        setMode(SIDENAV_MODES.print);
        setSelectedIds(ids => ids.length ? ids : [selectedArticleId]);
    };

    const handleProcessClick = () => {
        setMode(SIDENAV_MODES.llm);
        setSelectedIds(ids => ids.length ? ids : [selectedArticleId]);
        setStatus(LLM_STATES.config);
    };

    /* ──────────────────────────────────────────────────────────────── */
    /*   Cleanup (stop polling, reset state)                           */
    /* ──────────────────────────────────────────────────────────────── */
    const cleanup = useCallback(() => {
        jobRef.current = null;
        setJobActive(false);
        setJobProgress(0);
        setSelectedIds([]);
        setJobResultUrl(null);
        setMode(SIDENAV_MODES.none);
        resetLLM();
    }, [resetLLM]);

    useEffect(() => {
        if (mode === SIDENAV_MODES.llm && status === LLM_STATES.idle) cleanup();
    }, [cleanup, mode, status]);

    const handleCancel = async () => {
        const job = jobRef.current;
        jobAbortRef.current?.abort();
        if ((mode !== SIDENAV_MODES.none) && job) {
            try {
                await secureFetch(job.groupId
                    ? `${base}/jobs/groups/${job.groupId}/stop`
                    : `${base}/jobs/${job.jobId}/stop`, {
                    method: 'POST',
                    credentials: 'include',
                }, true);
            } catch (err) {
                console.error('Cancel job failed', err);
                notifyError('Could not cancel job');
            }
        }

        cleanup();
    };

    /**
     * Fetches all article IDs and uses them to sort the selected IDs
     */
    const collectArticleIdsFromSelection = useCallback(async () => {
        const qs = selectedRelease.toSearchParams();
        const res = await secureFetch(`${base}/toc/all-article-ids?${qs.toString()}`);
        if (!res.ok) throw new Error(await res.text());

        const { toc_node_ids } = await res.json();

        const selectedIdSet = new Set(selectedIds);
        return toc_node_ids.filter(nodeId => selectedIdSet.has(nodeId));
    }, [selectedIds, selectedRelease]);

    /**
     * Runs a job and waits for its completion using the side navigation context
     */
    const runJob = useCallback(async ({ setup, start, completion, maxArticleIds }) => {
        if (jobActive) return null;

        // Resolve selection to concrete articleIds (server-backed expansion).
        const articleIds = await collectArticleIdsFromSelection();
        if (!articleIds.length) {
            notifyError('No articles found in the current selection.');
            cleanup();
            return null;
        }

        if (maxArticleIds !== null && articleIds.length > maxArticleIds) {
            notifyError(`Article selection is limited to ${maxArticleIds} articles at a time. ` +
                'Please select fewer articles and try again.');
            return null;
        }

        setJobProgress(0);
        setJobResultUrl(null);
        setJobActive(true);

        setup();

        // Create a now abort controller
        const controller = new AbortController();
        jobAbortRef.current = controller;

        let result = null;

        try {
            result = await start({
                articleIds,
                selectedRelease,
                jobRef,
                signal: controller.signal,
            });

            if (result !== null && !controller.signal.aborted) {
                await completion(result);
                setJobProgress(100);
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                notifyError(err.message || 'Job failed!');
            }
            cleanup();
        } finally {
            jobAbortRef.current = null;
            setJobActive(false);
        }
    }, [cleanup, collectArticleIdsFromSelection, jobActive, notifyError, selectedRelease]);

    /* ------------------------------------------------------------------ */
    /*   PRINT                                                            */
    /* ------------------------------------------------------------------ */
    const handleConfirmPrint = () => runJob({
        setup: () => { },
        start: args => generatePdf({
            ...args, onProgress: (progress) => {
                progress = progress.toFixed(2);
                if (progress > jobProgress) {
                    setJobProgress(progress);
                }
            }
        }),
        completion: result => setJobResultUrl(result),
        maxArticleIds: null,
    });

    /* ──────────────────────────────────────────────────────────────── */
    /*   LLM processing                                                */
    /* ──────────────────────────────────────────────────────────────── */
    const handleConfirmProcess = useCallback(prompt => runJob({
        setup: () => {
            setResultUrl(null);
            setProgress(0);
            setStatus(LLM_STATES.running);
        },
        start: args => generateSummary({
            ...args, prompt, onProgress: (progress) => {
                progress = progress.toFixed(2);
                setJobProgress(previous => Math.max(previous, progress));
                setProgress(previous => Math.max(previous, progress));
            }
        }),
        completion: async result => {
            const content = await secureFetch(result, { method: 'GET' }, true);
            const answer = (await content.json()).answer;
            setStatus(LLM_STATES.done);
            setResultMarkdown(answer);
        },
        maxArticleIds: 200,
    }), [runJob, setProgress, setResultMarkdown, setResultUrl, setStatus]);

    // Register a current job handler for the separate prompt panel.
    useEffect(() => {
        if (mode === SIDENAV_MODES.llm) setStartProcess(() => handleConfirmProcess);
    }, [handleConfirmProcess, mode, setStartProcess]);

    const handleSelectAll = async () => {
        if (selectingAll) return;
        setSelectingAll(true);
        try {
            const qs = selectedRelease.toSearchParams();
            const res = await secureFetch(`${base}/toc/all-article-ids?${qs.toString()}`);
            if (!res.ok) throw new Error(await res.text());
            const { toc_node_ids } = await res.json();
            setSelectedIds(toc_node_ids);
            if (mode === 'llm') {
                setSelected(toc_node_ids.map(x => ({ _id: x, title: nodeMap.get(x)?.title || x })));
            }
        } catch (err) {
            notifyError(err.message || 'Select All failed');
        } finally {
            setSelectingAll(false);
        }
    };

    const handleClearSelection = () => {
        setSelectedIds([]);
        if (mode === 'llm') setSelected([]);
    };

    /* ──────────────────────────────────────────────────────────────── */
    /*   Render helpers                                                */
    /* ──────────────────────────────────────────────────────────────── */
    const renderToc = () => {
        const common = {
            selectedArticleId,
            activePath,
            selectedIds,
            mode,

            // Checkbox-Klick: NUR Auswahl toggeln (kein Inhalt laden)
            toggleSelect: (id) => {
                toggleSubtree(id);
            },

            // Zeilen-Klick (nur im Normalmodus relevant): Inhalt laden
            onArticleSelect: (id) => {
                openArticle(id); // lädt den Inhalt rechts
            },
        };

        return (
            <TocList
                items={tocData}
                defaultOpenLevels={defaultOpenLevels}
                {...common}
            />
        );
    };

    /* ──────────────────────────────────────────────────────────────── */
    /*   Drawer content                                                */
    /* ──────────────────────────────────────────────────────────────── */
    const renderDrawerContent = (scrollBoxRef, withResizeHandle = true) => (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%', minHeight: 0,
            marginTop: { xs: 0, sm: '56px' },
            bgcolor: 'background.default',
            color: 'text.primary',
        }}>
            <TocHeaderBar
                mobile={isMobile && !withResizeHandle}
                selectedIds={selectedIds}
                jobActive={jobActive}
                jobProgress={jobProgress}
                jobResultUrl={mode === SIDENAV_MODES.print ? jobResultUrl : resultUrl} // <— Select correct url Print / LLM
                onOpenPdf={() => {
                    if (mode !== SIDENAV_MODES.print) return;
                    if (jobResultUrl) window.open(jobResultUrl, '_blank');
                }}
                onPrintClick={handlePrintClick}
                onCancelClick={handleCancel}
                onConfirmClick={mode === SIDENAV_MODES.print ? handleConfirmPrint : undefined}
                toggleDarkMode={toggleDarkMode}
                mode={mode}
                isAdmin={isAdmin}
                onProcessClick={handleProcessClick}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                selectingAll={selectingAll}
            />

            {/* Small progress bar for Jobs */}
            {jobActive && (
                <LinearProgress
                    variant="determinate"
                    value={jobProgress}
                    sx={{
                        backgroundColor: darkMode ? '#00304d' : undefined,
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: 'progress.main',
                        },
                    }}
                />
            )}

            <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
                {/* TOC list */}
                <Box
                    ref={scrollBoxRef}
                    sx={{
                        flex: 1, minHeight: 0, overflowY: 'auto', pr: 0,
                        overflowAnchor: 'none',
                        scrollbarGutter: 'stable',
                        color: darkMode ? '#fff' : 'inherit',
                        '&::-webkit-scrollbar': { width: 8 },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: darkMode ? '#444' : '#9aa',
                            borderRadius: 4,
                        },
                        scrollbarWidth: 'thin',
                        scrollbarColor: darkMode ? '#444 transparent' : '#9aa transparent',
                    }}
                >
                    {renderToc()}
                </Box>

                {/* resize handle */}
                {withResizeHandle && (
                    <Box
                        ref={resizeHandleRef}
                        onPointerDown={startResize}
                        sx={{
                            width: 4, flexShrink: 0, cursor: 'col-resize', position: 'relative',
                            backgroundColor: 'background.default',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0, top: 0, bottom: 0, width: 1,
                                backgroundColor: 'background.default',
                            },
                            transition: 'background-color 120ms',
                            '&:hover': { backgroundColor: theme.palette.background.default },
                            userSelect: 'none', touchAction: 'none',
                        }}
                    />
                )}
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMsg}
                sx={{ color: darkMode ? '#fff' : 'inherit' }}
            />
        </Box>
    );

    /* ──────────────────────────────────────────────────────────────── */
    /*   Two drawers (mobile / desktop)                                */
    /* ──────────────────────────────────────────────────────────────── */
    return (
        <>
            {/* mobile */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={theme => ({
                    display: { xs: 'block', sm: 'none' },
                    width: 'min(350px, 100vw)',
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: 'min(350px, 100vw)',
                        top: theme.mixins.toolbar.minHeight,
                        height: `calc(100% - ${theme.mixins.toolbar.minHeight}px)`,
                        bgcolor: darkMode ? theme.palette.primary.main : undefined,
                        color: darkMode ? '#fff' : undefined,
                    },
                })}
            >
                {renderDrawerContent(mobileScrollRef, false)}
            </Drawer>

            {/* desktop */}
            <Drawer
                variant="permanent"
                sx={theme => ({
                    width: drawerWidth, flexShrink: 0, display: { xs: 'none', sm: 'block' },
                    '& .MuiDrawer-paper': {
                        width: drawerWidth, boxSizing: 'border-box',
                        top: 0, height: '100%', overflow: 'hidden',
                        display: 'flex', flexDirection: 'column',
                        bgcolor: darkMode ? theme.palette.primary.main : undefined,
                        color: darkMode ? '#fff' : undefined,
                    },
                })}
            >
                {renderDrawerContent(desktopScrollRef, true)}
            </Drawer>
        </>
    );
}
